import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Agent } from '../types';
import {
  KnowledgeBase,
  KnowledgeDoc,
  listKnowledgeBases,
  createKnowledgeBase,
  renameKnowledgeBase,
  deleteKnowledgeBase,
  listKnowledgeDocs,
  uploadKnowledgeFile,
  deleteKnowledgeDoc,
  listAgents,
} from '../api/client';

/**
 * Global, reusable knowledge bases: create a base, fill it with documents,
 * then attach it to any number of agents on the agent page.
 */
export function KnowledgePage() {
  const [bases, setBases] = useState<KnowledgeBase[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingBase, setEditingBase] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  const [openBase, setOpenBase] = useState<string | null>(null);
  const [docs, setDocs] = useState<KnowledgeDoc[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reverse of agent.knowledgeBaseIds: which agents each base is attached to.
  const agentsByBase = useMemo(() => {
    const map = new Map<string, Agent[]>();
    for (const base of bases) {
      map.set(
        base.id,
        agents.filter((a) => a.knowledgeBaseIds?.includes(base.id)),
      );
    }
    return map;
  }, [bases, agents]);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [loadedBases, loadedAgents] = await Promise.all([
        listKnowledgeBases(),
        listAgents().catch(() => [] as Agent[]),
      ]);
      setBases(loadedBases);
      setAgents(loadedAgents);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const base = await createKnowledgeBase(newName.trim(), newDescription.trim() || undefined);
      setBases((prev) => [...prev, base]);
      setNewName('');
      setNewDescription('');
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteBase = async (kbId: string) => {
    if (!window.confirm('Delete this knowledge base with all its documents? Agents using it will lose this knowledge.')) return;
    setError(null);
    try {
      await deleteKnowledgeBase(kbId);
      setBases((prev) => prev.filter((b) => b.id !== kbId));
      if (openBase === kbId) setOpenBase(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message);
    }
  };

  const startRename = (base: KnowledgeBase) => {
    setEditingBase(base.id);
    setEditName(base.name);
  };

  const cancelRename = () => {
    setEditingBase(null);
    setEditName('');
  };

  const handleRename = async (kbId: string) => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    setSavingRename(true);
    setError(null);
    try {
      const updated = await renameKnowledgeBase(kbId, trimmed);
      setBases((prev) => prev.map((b) => (b.id === kbId ? { ...b, name: updated.name } : b)));
      cancelRename();
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setSavingRename(false);
    }
  };

  const toggleBase = async (kbId: string) => {
    if (openBase === kbId) {
      setOpenBase(null);
      return;
    }
    setOpenBase(kbId);
    setDocsLoading(true);
    try {
      setDocs(await listKnowledgeDocs(kbId));
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message);
      setDocs([]);
    } finally {
      setDocsLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !openBase) return;
    setUploading(true);
    setError(null);
    try {
      const doc = await uploadKnowledgeFile(openBase, file);
      setDocs((prev) => [...prev, doc]);
      setBases((prev) => prev.map((b) => (b.id === openBase ? { ...b, docCount: b.docCount + 1 } : b)));
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    if (!openBase || !window.confirm('Remove this document?')) return;
    setError(null);
    try {
      await deleteKnowledgeDoc(openBase, docId);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
      setBases((prev) => prev.map((b) => (b.id === openBase ? { ...b, docCount: Math.max(0, b.docCount - 1) } : b)));
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  return (
    <div style={s.container}>
      <h2 style={s.heading}>Knowledge Bases</h2>
      <span style={s.hint}>
        Reusable document collections. Upload files here, then attach a base to any agent on its page — several agents can share the same base.
      </span>

      {/* Create */}
      <div style={s.section}>
        <h3 style={s.sub}>New knowledge base</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...s.input, flex: 1 }} placeholder="Name (e.g. Product docs)" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <input style={{ ...s.input, flex: 2 }} placeholder="Description (optional)" value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
          <button style={s.saveBtn} onClick={handleCreate} disabled={creating || !newName.trim()}>
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {/* List */}
      {loading ? (
        <div style={s.muted}>Loading...</div>
      ) : bases.length === 0 ? (
        <div style={s.muted}>No knowledge bases yet</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bases.map((base) => {
            const attachedAgents = agentsByBase.get(base.id) || [];
            return (
            <div key={base.id} style={s.section}>
              <div
                style={{ ...s.baseRow, cursor: editingBase === base.id ? 'default' : 'pointer' }}
                onClick={() => editingBase === base.id ? undefined : toggleBase(base.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {editingBase === base.id ? (
                    <input
                      style={{ ...s.input, fontSize: 14 }}
                      value={editName}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRename(base.id);
                        if (e.key === 'Escape') cancelRename();
                      }}
                    />
                  ) : (
                    <strong style={{ fontSize: 14, color: '#1a1a1a' }}>{base.name}</strong>
                  )}
                  <span style={{ fontSize: 12, color: '#888' }}>{base.docCount} docs</span>
                  <span style={{ fontSize: 12, color: attachedAgents.length ? '#4f46e5' : '#bbb' }}>
                    {attachedAgents.length ? `${attachedAgents.length} agent${attachedAgents.length > 1 ? 's' : ''}` : 'Not attached'}
                  </span>
                  {base.description && <span style={{ fontSize: 12, color: '#888' }}>{base.description}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {editingBase === base.id ? (
                    <>
                      <button style={s.saveBtnSmall} onClick={(e) => { e.stopPropagation(); handleRename(base.id); }} disabled={savingRename || !editName.trim()}>
                        {savingRename ? 'Saving...' : 'Save'}
                      </button>
                      <button style={s.smallBtn} onClick={(e) => { e.stopPropagation(); cancelRename(); }}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 11, color: '#aaa' }}>{new Date(base.createdAt).toLocaleDateString()}</span>
                      <button style={s.smallBtn} onClick={(e) => { e.stopPropagation(); startRename(base); }}>Rename</button>
                      <button style={s.delBtn} onClick={(e) => { e.stopPropagation(); handleDeleteBase(base.id); }}>Delete</button>
                    </>
                  )}
                </div>
              </div>

              {openBase === base.id && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 12, color: '#555', fontWeight: 600, display: 'block', marginBottom: 6 }}>Used by agents</span>
                    {attachedAgents.length === 0 ? (
                      <span style={s.muted}>Not attached to any agent</span>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {attachedAgents.map((a) => (
                          <Link key={a.id} to={`/agents/${a.id}`} style={s.agentChip}>{a.name}</Link>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>Documents</span>
                    <label style={{ ...s.smallBtn, opacity: uploading ? 0.6 : 1 }}>
                      {uploading ? 'Indexing...' : '+ Upload file'}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.docx,.txt,.md,.csv,.json,.html"
                        style={{ display: 'none' }}
                        disabled={uploading}
                        onChange={handleUpload}
                      />
                    </label>
                  </div>
                  <span style={{ ...s.hint, marginBottom: 6 }}>PDF, DOCX, TXT, MD, CSV, JSON, HTML — max 20 MB.</span>
                  {docsLoading ? (
                    <div style={s.muted}>Loading...</div>
                  ) : docs.length === 0 ? (
                    <div style={s.muted}>No documents yet</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {docs.map((doc) => (
                        <div key={doc.id} style={s.docCard}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 13, color: '#333' }}>{doc.filename}</span>
                            <span style={{ fontSize: 11, color: '#888' }}>
                              {(doc.sizeBytes / 1024).toFixed(1)} KB &middot; {doc.chunks} chunks
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, color: '#aaa' }}>{new Date(doc.createdAt).toLocaleString()}</span>
                            <button style={s.delBtn} onClick={() => handleDeleteDoc(doc.id)}>x</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { padding: 20, flex: 1, overflowY: 'auto' },
  heading: { margin: '0 0 6px', fontSize: 20, color: '#1a1a1a' },
  hint: { fontSize: 12, color: '#888', display: 'block', marginBottom: 14 },
  sub: { margin: '0 0 10px', fontSize: 15, fontWeight: 600, color: '#1a1a1a' },
  section: { marginBottom: 12, padding: '14px 18px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafafa' },
  input: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, outline: 'none' },
  saveBtn: { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  saveBtnSmall: { padding: '4px 14px', borderRadius: 6, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  smallBtn: { padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#555' },
  delBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer' },
  error: { padding: '8px 12px', background: '#fff0f0', border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13, marginBottom: 12 },
  muted: { color: '#888', fontSize: 13 },
  baseRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  docCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 },
  agentChip: { padding: '3px 10px', borderRadius: 999, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', fontSize: 12, textDecoration: 'none' },
};
